"use client";

/**
 * Cards View
 * Renders schedule items grouped by date in a responsive card grid.
 */

import type { ScheduleItemRow, ScheduleLabelRow } from "../../../../_logic/types/db-types";
import { ScheduleItemDisplay } from "./ScheduleItemDisplay";
import { DateHeading } from "./DateHeading";
import { ItemDeleteButton } from "./ItemDeleteButton";
import { getFirstLabelColor } from "./label-utils";
import {
  getScheduleItemRegistrationState,
  type ViewerRegistrationKeys,
} from "../../../../_logic/utils/schedule-registration-match";

interface CardsViewProps {
  dateGroups: { date: string | null; items: ScheduleItemRow[] }[];
  labels: ScheduleLabelRow[];
  /** Pre-computed label lookup map (computed once in ScheduleRenderer) */
  labelMap: Map<number, ScheduleLabelRow>;
  primaryColor: string;
  onDelete?: (item: ScheduleItemRow) => void;
  /** When provided, items become clickable (used in public view for detail modal) */
  onItemClick?: (item: ScheduleItemRow) => void;
  /** Page-wide IANA timezone for rendering times. NULL = floating time. */
  timeZone: string | null;
  isPublic?: boolean;
  /** Viewer's confirmed registrations. `null` = no logged-in attendee. */
  viewerRegistrations: ViewerRegistrationKeys | null;
}

/** Renders schedule items as a responsive grid of cards grouped by date */
export function CardsView({
  dateGroups,
  labels,
  labelMap,
  primaryColor,
  onDelete,
  onItemClick,
  timeZone,
  isPublic = false,
  viewerRegistrations,
}: CardsViewProps) {
  const gridClass = isPublic ? "sm:grid-cols-2 md:grid-cols-3" : "@sm:grid-cols-2 @md:grid-cols-3";
  return (
    <div className="flex flex-col gap-6">
      {dateGroups.map((group) => (
        <div key={group.date ?? "no-date"} className="flex flex-col gap-3">
          <DateHeading date={group.date} />
          <div className={`grid gap-3 ${gridClass}`}>
            {group.items.map((item) => {
              const borderColor = getFirstLabelColor(item, labelMap) ?? primaryColor;
              const registrationState = getScheduleItemRegistrationState(item, viewerRegistrations);
              const registrationRing =
                registrationState === "registered"
                  ? "ring-1 ring-emerald-500/40"
                  : registrationState === "waitlisted"
                    ? "ring-1 ring-amber-500/40"
                    : "";
              return (
                <div
                  key={item.id}
                  className={`group/item overflow-hidden rounded-lg border border-gray-200 bg-white ${onItemClick ? "cursor-pointer touch-manipulation transition-shadow hover:shadow-md" : ""} ${registrationRing}`}
                  onClick={onItemClick ? () => onItemClick(item) : undefined}
                  role={onItemClick ? "button" : undefined}
                  tabIndex={onItemClick ? 0 : undefined}
                  onKeyDown={
                    onItemClick
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onItemClick(item);
                          }
                        }
                      : undefined
                  }
                >
                  <div className="h-1 w-full" style={{ backgroundColor: borderColor }} />
                  <div className="flex items-start justify-between gap-1.5 p-3">
                    <div className="flex min-w-0 flex-col gap-1.5">
                      <ScheduleItemDisplay
                        item={item}
                        labels={labels}
                        showDate
                        timeZone={timeZone}
                        isPublic={isPublic}
                        viewerRegistrations={viewerRegistrations}
                      />
                    </div>
                    {onDelete && <ItemDeleteButton item={item} onDelete={onDelete} />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
